package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseDown
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.setVariable
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.components.overlay.OverlayVars
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import kotlin.random.Random
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.yield
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

enum class LModalSize(val maxWidth: Double) {
    SM(28.0),
    MD(40.0),
    LG(56.0),
}

enum class LModalMotionPolicy {
    STANDARD,
    INSTANT,
}

val LModalStyle = CssStyle {
    base {
        Modifier
            .width(100.percent)
            .maxHeight(90.vh)
            .overflow(Overflow.Auto)
            .backgroundColor(colorMode.toSitePalette().surfaceRaised)
            .color(colorMode.toSitePalette().textPrimary)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .borderRadius(Radius.Modal)
            // [Elevation.Floating], not [Elevation.Overlay], even though the dialog is the topmost
            // surface in the product: it is the only thing above a scrim that already darkens
            // everything behind it, so the shadow has nothing left to separate it from and a
            // heavier one only muddies the scrim at the dialog's edge.
            .elevation(Elevation.Floating)
            .styleModifier {
                property(
                    "transition",
                    "opacity ${Motion.OverlayEnterMs}ms ${Motion.EaseEntrance}, " +
                        "transform ${Motion.OverlayEnterMs}ms ${Motion.EaseEntrance}",
                )
            }
    }
    cssRule("[data-presence=\"entering\"]") {
        Modifier.styleModifier {
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
        }
    }
    cssRule("[data-presence=\"exiting\"]") {
        Modifier.styleModifier {
            property("opacity", "0")
            property(
                "transform",
                "translateY(${Motion.PopoverTravelPx}px) scale(${Motion.PopoverEnterScale})",
            )
            property(
                "transition",
                "opacity ${Motion.OverlayExitMs}ms ${Motion.EaseIn}, " +
                    "transform ${Motion.OverlayExitMs}ms ${Motion.EaseIn}",
            )
            property("pointer-events", "none")
        }
    }
}

val LModalCloseStyle = CssStyle {
    base {
        Modifier
            .backgroundColor(Color.rgba(0, 0, 0, 0f))
            .color(colorMode.toSitePalette().textSecondary)
            .border(1.px, LineStyle.Solid, Color.rgba(0, 0, 0, 0f))
            .borderRadius(Radius.Control)
            .padding(Space.XS)
            .cursor(Cursor.Pointer)
            // `Transition.of` without a `timingFunction` omits it from the generated CSS, so this
            // one button hovered on the browser default `ease` while every other interactive
            // surface in the product uses `Motion.EaseOut`. The helper is the whole reason a curve
            // cannot be forgotten.
            .transitionColors(durationMs = Motion.FastMs)
    }
    hover {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .color(colorMode.toSitePalette().textPrimary)
    }
    focusVisible {
        Modifier.outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent).styleModifier {
            property("outline-offset", "2px")
        }
    }
}

@Composable
fun LModal(
    open: Boolean,
    title: String,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
    size: LModalSize = LModalSize.MD,
    motionPolicy: LModalMotionPolicy = LModalMotionPolicy.STANDARD,
    actions: (@Composable RowScope.() -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    val modalId = remember { "lingvo-modal-${Random.nextInt(1_000_000)}" }
    val instant = motionPolicy == LModalMotionPolicy.INSTANT
    val presence = rememberLPresence(
        open = open,
        exitDurationMillis = if (instant) Motion.InstantMs else Motion.OverlayExitMs,
        settleImmediately = instant,
    )
    val latestOpen by rememberUpdatedState(open)

    DisposableEffect(presence.rendered, modalId) {
        if (!presence.rendered) {
            onDispose { }
        } else {
            val previousFocus = document.activeElement as? HTMLElement
            val previousOverflow = document.body?.style?.getPropertyValue("overflow").orEmpty()
            document.body?.style?.setProperty("overflow", "hidden")
            val listener = EventListener { event ->
                val keyboardEvent = event as? KeyboardEvent ?: return@EventListener
                if (!latestOpen) return@EventListener
                when (keyboardEvent.key) {
                    "Escape" -> onClose()
                    "Tab" -> trapModalFocus(modalId, keyboardEvent)
                }
            }
            window.addEventListener("keydown", listener)
            onDispose {
                window.removeEventListener("keydown", listener)
                document.body?.style?.setProperty("overflow", previousOverflow)
                // **Give focus back only if nobody else has claimed it.**
                //
                // Restoring unconditionally overwrites a deliberate handoff. A confirmation that
                // closes the surface behind it puts focus on the control that surface came from,
                // and this then dragged focus back onto a button inside the disappearing subtree —
                // leaving a keyboard user on `body` a frame later, with no way back but Tab from
                // the top. Focus still sitting inside the dialog, or on nothing at all, means the
                // dialog is the last thing that had it and restoring is right.
                val active = document.activeElement
                val claimedElsewhere = active != null &&
                    active != document.body &&
                    document.getElementById(modalId)?.contains(active) != true
                if (!claimedElsewhere && previousFocus?.isConnectedToDocument() == true) {
                    previousFocus.focus()
                }
            }
        }
    }

    if (!presence.rendered) return

    val exiting = presence.phase == LPresencePhase.EXITING
    val presenceValue = if (instant) LPresencePhase.PRESENT.attributeValue else presence.phase.attributeValue

    // **Dismiss on a press that starts on the scrim, not on a click that merely ends there.**
    //
    // A `click` is dispatched at the nearest common ancestor of the press and the release, so a drag
    // that begins inside the dialog and ends outside it fires `click` on the scrim itself — and the
    // panel's own `stopPropagation` cannot help, because the scrim *is* the target, not an ancestor
    // of one. Selecting a word to fix a typo in a confirmation field and releasing a few pixels past
    // the edge therefore closed the dialog. `LConfirmDialog` remembers its typed phrase keyed on
    // `open`, so the phrase went with it and the whole confirmation had to be typed again.
    var pressStartedOnScrim by remember { mutableStateOf(false) }

    Overlay(
        Modifier
            .setVariable(OverlayVars.BackgroundColor, ScrimVars.Modal.value())
            .zIndex(1000)
            .padding(Space.LG)
            .attr("data-presence", presenceValue)
            .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier)
            .styleModifier {
                property(
                    "transition",
                    "opacity ${if (exiting) Motion.OverlayExitMs else Motion.OverlayEnterMs}ms " +
                        if (exiting) Motion.EaseIn else Motion.EaseEntrance,
                )
                // **The scrim starts transparent, so the transition above actually runs.**
                //
                // It declared a 240ms opacity transition and only ever set `opacity: 0` on the way
                // out, so there was no value to transition *from* on the way in: a 62%-black
                // full-screen dim snapped on in one frame while the panel it belongs to eased in
                // over 240ms. The exit faded; only the entrance did not, which is why it read as
                // abrupt rather than as missing.
                //
                // The panel animates transform alone, deliberately — "the fade is gone, and its
                // absence is the point". A scrim has no transform and nothing to travel; opacity is
                // the only entrance it can have, and sharing the panel's lifecycle is what the
                // contract asks of it.
                if (presence.phase == LPresencePhase.ENTERING) property("opacity", "0")
                if (exiting) {
                    property("opacity", "0")
                    property("pointer-events", "none")
                }
            }
            .onMouseDown { event ->
                val from = event.target as? HTMLElement
                pressStartedOnScrim = from == null ||
                    document.getElementById(modalId)?.contains(from) != true
            }
            .onClick { if (pressStartedOnScrim) onClose() },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            LModalStyle.toModifier()
                .maxWidth(size.maxWidth.cssRem)
                .role("dialog")
                .attr("id", modalId)
                .attr("tabindex", "-1")
                .attr("aria-modal", "true")
                .attr("aria-label", title)
                .attr("data-presence", presenceValue)
                .then(if (exiting) Modifier.attr("inert", "").attr("aria-hidden", "true") else Modifier)
                .onClick { it.stopPropagation() }
                .then(modifier),
        ) {
            // Overlay defers its children: presence alone does not mean the dialog has mounted.
            // Start initial focus inside the mounted panel, then yield for its DOM to be applied.
            // The first control is the safe close action; content may subsequently focus a typed
            // confirmation or an explicit recovery action as it already does.
            LaunchedEffect(open, modalId) {
                if (open) {
                    yield()
                    val dialog = document.getElementById(modalId) as? HTMLElement
                    val firstFocusable = dialog?.querySelector(
                        "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), " +
                            "select:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])",
                    ) as? HTMLElement
                    (firstFocusable ?: dialog)?.focus()
                }
            }
            // **Three rules, one width.**
            //
            // A Kobweb `Column` is `align-items: flex-start`, so every child here was shrink-to-fit:
            // the header's hairline stopped at the end of the title, the footer's stopped at the end
            // of its buttons, and the panel's own border ran past both. At 1440 that was a header
            // rule ~110px short of the panel edge and a footer rule shorter still — three horizontal
            // lines at three lengths inside one 640px dialog. `box-sizing: border-box` is global
            // (see `initLingvoTheme`), so `width: 100%` puts the padding inside the rule rather than
            // outside it, which is what makes the rules span the panel's content box edge to edge.
            Row(
                Modifier
                    .width(100.percent)
                    // **A token between the title and the close control.** `Spacer` sets their
                    // resting distance and gives none of its own once the title is long enough to
                    // take the row: at 1440 "Approve 12 QA-clean strings" ran straight into the
                    // glyph. A gap is a floor the spacer cannot spend.
                    .gap(Space.LG)
                    .padding(topBottom = Space.LG, leftRight = Space.XXL)
                    .borderBottom(1.px, LineStyle.Solid, com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().border)
                    .attr("data-modal-band", "header"),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                H2(
                    Modifier
                        .displayFont()
                        .fontSize(TypeScale.HeadingSmall)
                        .fontWeight(700)
                        .margin(0.px)
                        .toAttrs(),
                ) {
                    Text(title)
                }
                Spacer()
                Button(
                    LModalCloseStyle.toModifier().toAttrs {
                        type(ButtonType.Button)
                        attr("aria-label", tr("ds.ds.lmodal.close_dialog", "Close dialog"))
                        onClick { onClose() }
                    },
                ) {
                    LCloseIcon()
                }
            }
            Column(
                Modifier
                    .width(100.percent)
                    .padding(Space.XXL)
                    .gap(Space.LG)
                    .attr("data-modal-band", "body"),
            ) {
                content()
            }
            if (actions != null) {
                Row(
                    Modifier
                        .width(100.percent)
                        .gap(Space.SM)
                        .padding(topBottom = Space.LG, leftRight = Space.XXL)
                        .borderTop(1.px, LineStyle.Solid, com.varabyte.kobweb.silk.theme.colors.ColorMode.current.toSitePalette().border)
                        .attr("data-modal-band", "footer"),
                    verticalAlignment = Alignment.CenterVertically,
                    content = actions,
                )
            }
        }
    }
}

/** True while the element is still in the document, so focus given to it can actually land. */
private fun HTMLElement.isConnectedToDocument(): Boolean = asDynamic().isConnected as? Boolean ?: true

private fun trapModalFocus(
    modalId: String,
    event: KeyboardEvent,
) {
    val dialog = document.getElementById(modalId) as? HTMLElement ?: return
    val nodes = dialog.querySelectorAll(
        "button:not(:disabled), input:not(:disabled), textarea:not(:disabled), " +
            "select:not(:disabled), a[href], [tabindex]:not([tabindex='-1'])",
    )
    val focusable = buildList {
        for (index in 0 until nodes.length) {
            (nodes.item(index) as? HTMLElement)?.let(::add)
        }
    }
    if (focusable.isEmpty()) {
        event.preventDefault()
        dialog.focus()
        return
    }
    val active = document.activeElement
    val first = focusable.first()
    val last = focusable.last()
    // **Both directions test the same two things.** The Shift branch already handled focus sitting
    // outside the dialog's focusable set — on the dialog container itself, or on a control that has
    // since become unfocusable — and the forward branch checked only `active == last`. So a forward
    // Tab from anywhere the set does not contain fell through to the browser's default and moved
    // into the page behind the modal, which is the one thing a trap exists to prevent, and it is
    // reachable the moment anything inside the dialog is disabled while it has focus.
    if (event.shiftKey && (active == first || active !in focusable)) {
        event.preventDefault()
        last.focus()
    } else if (!event.shiftKey && (active == last || active !in focusable)) {
        event.preventDefault()
        first.focus()
    }
}
